import React from "react";
import { useTranslation } from "react-i18next";

import { SelectOptions } from "../../../assets/data/options";
import { CommonSlider } from "../../../components/CommonSlider";
import { InvestmentInputTypeUnion, InvestmentTypeIntersection } from "../../../store/investment/types";
import { SubformProps } from "../../SubformProps";
import { InputFieldPropsUnion } from "../elements/types";
import { CheckboxInternal, IntegerFieldInternal, KeyUndefinedError, SelectFieldInternal } from "./InputField";

type InvestmentFieldDescriptor = {
  [key in keyof InputFieldPropsUnion]: any;
}

type Predefined =
  | 'id'
  | 'maxAmount'
  | 'minAmount'
  | 'saleId'
  | 'termsRevocation' // TODO(Marko): Should be handled as others and removed from this exception list
  | 'status' // TODO(Marko): Should be handled as others and removed from this exception list

export type InvestmentInputTypeKey = keyof Omit<InvestmentInputTypeUnion, Predefined>;

type InvestmentFieldProps = SubformProps<InvestmentTypeIntersection> & Partial<InvestmentFieldDescriptor> & {
  id: InvestmentInputTypeKey;
  noLabels?: boolean;
  index?: number;
}

export const InvestmentField = (props: InvestmentFieldProps) : JSX.Element | null => {
  // Subform Props

  const {
    index,
    setFieldValue,
    values,
    variant,
  } = props;

  // Generated Props

  const id = props.id as InvestmentInputTypeKey;

  // Hooks

  const { t } = useTranslation();

  // Building Component

  switch (id) {
    case "amountShares": {
      const text = t("Euros");
      const min = 'minAmount' in values && values.minAmount || 0;
      const max = 'maxAmount' in values && values.maxAmount || 0;
      const sliderMarks = [
        {
          value: min,
          label: `${min} €`,
        },
        {
          value: max,
          label: `${max} €`,
        }
      ];
      return variant === 'slider' ?
        <CommonSlider
          id="amountShares"
          values={values}
          setFieldValue={setFieldValue}
          min={min}
          max={max}
          marks={sliderMarks}
        />
        :
        <IntegerFieldInternal
          label={text}
          placeholder={text}
          { ...props }
          inputProps={{
            min,
            max
          }}
        />
      ;
    }
    case "documents": {
      return (
        <CheckboxInternal
          label={values[id][index].name}
          { ...props }
        />
      );
    }
    case "investingForThemselves": {
      return (
        <CheckboxInternal
          label={t("I represent that I am acting for my own account and in my own economic interest.")}
          { ...props }
        />
      );
    }
    case "paymentMethodId": {
      return (
        <SelectFieldInternal
          label={t("Payment method")}
          placeholder={t("Select...")}
          options={[]}
          { ...props }
        />
      );
    }
    case "paymentMethodType": {
      return (
        <SelectFieldInternal
          label={t("Payment method type")}
          placeholder={t("Select...")}
          options={SelectOptions().paymentMethodType}
          { ...props }
        />
      );
    }
    case "selfDisclosedAvailableCash": {
      return (
        <CheckboxInternal
          label={t("Yes, at the time of my investment I own freely disposable assets in the form of cash balances and financial instruments (securities, investment funds, asset investments, etc.) of at least 100,000.00 euros.")}
          { ...props }
        />
      );
    }
    case "selfDisclosedMonthlyNetSalary": {
      return (
        <CheckboxInternal
          label={t("Yes, the investment amount does not exceed twice the amount of my average monthly net income at the time of the investment. (If you invest more than once, the individual amounts will be added together).")}
          { ...props }
        />
      );
    }
    default: {
      KeyUndefinedError(id); // Compile-time check for unhandled cases
    }
  }

  return <></>;
}
